import React from "react";
import classNames from "classnames";
import { getDeadlockImagePath, shouldDisplayStat, formatUpgradeText, parseDescriptionText } from "lib/general-helper";

interface AbilityTooltipProps {
  ability: {
    name: string;
    description: string;
    t1Desc: string;
    t2Desc: string;
    t3Desc: string;
    abilityImage: string;
    stats: Record<string, string | number>;
    upgrades: Record<number, Record<string, string | number>>;
  };
}

const AbilityTooltip = (props: AbilityTooltipProps) => {
  const { ability } = props;

  const abilityIcon = getDeadlockImagePath(ability.abilityImage, ability.name);

  const processedDescription = parseDescriptionText(ability.description);

  const Tier1Description = ability.t1Desc;
  const Tier2Description = ability.t2Desc;
  const Tier3Description = ability.t3Desc;

  const upgrades = ability.upgrades;

  const abilityUpgrade1 = upgrades[0];
  const abilityUpgrade2 = upgrades[1];
  const abilityUpgrade3 = upgrades[2];

  // Extract stats
  const DPS = ability.stats?.DPS;
  const maxSlowPercent = ability.stats?.MaxSlowPercent;
  const maxSlowTime = ability.stats?.MaxSlowTime;
  const pathLength = ability.stats?.PathLength;
  const cooldown = ability.stats?.AbilityCooldown;
  const damage = ability.stats?.Damage;
  const movementSlow = ability.stats?.SlowPercent;
  const movementSlowActual = ability.stats?.MovementSlow;
  const slowDuration = ability.stats?.SlowDuration;
  const moveSpeedBonus = ability.stats?.MoveSpeedBonus;
  const sprintSpeedBonus = ability.stats?.SprintSpeedBonus;
  const icePathAuraDuration = ability.stats?.IcePathAuraDuration;
  const bonusHealthRegen = ability.stats?.BonusHealthRegen;
  const holdInPlaceDuration = ability.stats?.HoldInPlaceDuration;
  const whirlwindEvasionChance = ability.stats?.WhirlwindEvasionChance;
  const whirlwindDuration = ability.stats?.WhirlwindDuration;
  const radius = ability.stats?.Radius;
  const healthSteal = ability.stats?.HealthSteal;
  const bulletArmorReduction = ability.stats?.BulletArmorReduction;
  const launchWindowDuration = ability.stats?.LaunchWindowDuration;
  const debuffDuration = ability.stats?.DebuffDuration;
  const launchWindowCooldown = ability.stats?.LaunchWindowCooldown;
  const healingFactor = ability.stats?.HealingFactor;
  const nonHeroHealingFactor = ability.stats?.NonHeroHealingFactor;
  const procDamageBase = ability.stats?.ProcDamageBase;
  const maxStacks = ability.stats?.MaxStacks;
  const victimStackDuration = ability.stats?.VictimStackDuration;
  const revealDuration = ability.stats?.RevealDuration;
  const abilityChannelTime = ability.stats?.AbilityChannelTime;
  const bonusMoveSpeed = ability.stats?.BonusMoveSpeed;
  const bonusFireRate = ability.stats?.BonusFireRate;
  const movementSpeedBonusDuration = ability.stats?.MovementSpeedBonusDuration;
  const bulletLifestealPercent = ability.stats?.BulletLifestealPercent;
  const tetherSharedHealPct = ability.stats?.TetherSharedHealPct;
  const totalTetherTargets = ability.stats?.TotalTetherTargets;
  const stunDuration = ability.stats?.StunDuration;
  const maxHealthRegen = ability.stats?.MaxHealthRegen;
  const explodeDamage = ability.stats?.ExplodeDamage;
  const airDropBulletShield = ability.stats?.AirDropBulletShield;
  const flyingBulletResist = ability.stats?.FlyingBulletResist;
  const buffDuration = ability.stats?.BuffDuration;
  const onLandDamageRadius = ability.stats?.OnLandDamageRadius;
  const selfDamage = ability.stats?.SelfDamage;
  const armingDuration = ability.stats?.ArmingDuration;
  const lifeDrainPerSecond = ability.stats?.LifeDrainPerSecond;
  const lifeDrainHealthMult = ability.stats?.LifeDrainHealthMult;
  const maxRange = ability.stats?.MaxRange;
  const healthToDamage = ability.stats?.HealthToDamage;
  const numBloodShards = ability.stats?.NumBloodShards;
  const vulnerabilityPerStack = ability.stats?.VulnerabilityPerStack;
  const moveSpeedPenaltyPerStack = ability.stats?.MoveSpeedPenaltyPerStack;
  const enemyMinHealthPct = ability.stats?.EnemyMinHealthPct;
  const minHealthTakenPct = ability.stats?.MinHealthTakenPct;
  const sleepWakeUpDelay = ability.stats?.SleepWakeUpDelay;
  const minimumSleepTime = ability.stats?.MinimumSleepTime;
  const sleepDuration = ability.stats?.SleepDuration;
  const invisFadeToDuration = ability.stats?.InvisFadeToDuration;
  const spottedRadius = ability.stats?.SpottedRadius;
  const invisMoveSpeedMod = ability.stats?.InvisMoveSpeedMod;
  const damageBonusFixedPerStack = ability.stats?.DamageBonusFixedPerStack;
  const turretDPS = ability.stats?.TurretDPS;
  const turretAttackRange = ability.stats?.TurretAttackRange;
  const turretLifetime = ability.stats?.TurretLifetime;
  const externalBonusHealthRegen = ability.stats?.ExternalBonusHealthRegen;
  const healRadius = ability.stats?.HealRadius;
  const minRange = ability.stats?.MinRange;
  const wallImpactRange = ability.stats?.WallImpactRange;
  const damagePerRocket = ability.stats?.DamagePerRocket;
  const grenadesPerSecond = ability.stats?.GrenadesPerSecond;
  const explosionRadius = ability.stats?.ExplosionRadius;
  const minDistance = ability.stats?.MinDistance;
  const pulseDamage = ability.stats?.PulseDamage;
  const damageAmplificationPerStack = ability.stats?.DamageAmplificationPerStack;
  const pulseInterval = ability.stats?.PulseInterval;
  const movementSlowDuration = ability.stats?.MovementSlowDuration;
  const timeScaleDuration = ability.stats?.TimeScaleDuration;
  const movementSlowPct = ability.stats?.MovementSlowPct;
  const timeWallWidth = ability.stats?.TimeWallWidth;
  const timeWallHeight = ability.stats?.TimeWallHeight;
  const maxBonusBulletDamage = ability.stats?.MaxBonusBulletDamage;
  const maxSlowDuration = ability.stats?.MaxSlowDuration;
  const speedChange = ability.stats?.SpeedChange;
  const baseBulletDamage = ability.stats?.BaseBulletDamage;
  const headshotBonus = ability.stats?.HeadshotBonus;
  const minBonusBulletDamage = ability.stats?.MinBonusBulletDamage;
  const minSlowDuration = ability.stats?.MinSlowDuration;
  const maxChargeDuration = ability.stats?.MaxChargeDuration;
  const speedBoostDuration = ability.stats?.SpeedBoostDuration;
  const swapTotalDamage = ability.stats?.SwapTotalDamage;
  const spellLifesteal = ability.stats?.SpellLifesteal;
  const landingDamage = ability.stats?.LandingDamage;
  const fireRateSlow = ability.stats?.FireRateSlow;
  const explodeDebuffDuration = ability.stats?.ExplodeDebuffDuration;
  const fuseTime = ability.stats?.FuseTime;
  const beamLength = ability.stats?.BeamLength;
  const beamWidth = ability.stats?.BeamWidth;
  const damageAmplification = ability.stats?.DamageAmplification;
  const speedBurstSpeed = ability.stats?.SpeedBurstSpeed;
  const flameAuraDPS = ability.stats?.FlameAuraDPS;
  const dashTime = ability.stats?.DashTime;
  const dashSpeed = ability.stats?.DashSpeed;
  const groundFlameDuration = ability.stats?.GroundFlameDuration;
  const flameAuraRadius = ability.stats?.FlameAuraRadius;
  const burnDuration = ability.stats?.BurnDuration;
  const buildUpBulletPercentPerHit = ability.stats?.BuildUpBulletPercentPerHit;
  const critBuildup = ability.stats?.CritBuildup;
  const buildUpDuration = ability.stats?.BuildUpDuration;
  const explodeDelay = ability.stats?.ExplodeDelay;
  const fullChargeDamage = ability.stats?.FullChargeDamage;
  const slashLength = ability.stats?.SlashLength;
  const healFixedHealth = ability.stats?.HealFixedHealth;
  const abilitySpeedPct = ability.stats?.AbilitySpeedPct;
  const techResist = ability.stats?.TechResist;
  const bulletResist = ability.stats?.BulletResist;
  const shadowFormDurationOnKill = ability.stats?.ShadowFormDurationOnKill;
  const chainDuration = ability.stats?.ChainDuration;
  const captureRadius = ability.stats?.CaptureRadius;
  const chainLength = ability.stats?.ChainLength;
  const magicDamagePerBullet = ability.stats?.MagicDamagePerBullet;
  const dotHealthPercent = ability.stats?.DotHealthPercent;
  const impactDamage = ability.stats?.ImpactDamage;
  const impactRadius = ability.stats?.ImpactRadius;
  const bulletResistReduction = ability.stats?.BulletResistReduction;
  const techArmorDamageReduction = ability.stats?.TechArmorDamageReduction;
  const lowHealthEnemyDamageBonus = ability.stats?.LowHealthEnemyDamageBonus;
  const bonusGoldOnKill = ability.stats?.BonusGoldOnKill;
  const timeToFullCharge = ability.stats?.TimeToFullCharge;
  const minChargeDamagePercent = ability.stats?.MinChargeDamagePercent;
  const bleedDPSPerStack = ability.stats?.BleedDPSPerStack;
  const bleedDuration = ability.stats?.BleedDuration;
  const dashRange = ability.stats?.DashRange;
  const damagePctDeferred = ability.stats?.DamagePctDeferred;
  const deferredDamageDuration = ability.stats?.deferredDamageDuration;
  const deferClearPct = ability.stats?.DeferClearPct;
  const damagePctDeferredMaxRage = ability.stats?.DamagePctDeferredMaxRage;
  const enemyHealthPercent = ability.stats?.EnemyHealthPercent;
  const buffDamage = ability.stats?.BuffDamage;
  const puddleDuration = ability.stats?.PuddleDuration;
  const impactDuration = ability.stats?.ImpactDuration;
  const ballRadius = ability.stats?.BallRadius;
  const moveSpeedSlowPct = ability.stats?.MoveSpeedSlowPct;
  const weaponPowerDebuff = ability.stats?.WeaponPowerDebuff;
  const techShieldPoints = ability.stats?.TechShieldPoints;
  const moveSpeedBonusPct = ability.stats?.MoveSpeedBonusPct;
  const immobilizeDuration = ability.stats?.ImmobilizeDuration;
  const escapeTime = ability.stats?.EscapeTime;
  const escapeRange = ability.stats?.EscapeRange;
  const pulseDPS = ability.stats?.PulseDPS;
  const healthStealPctHero = ability.stats?.HealthStealPctHero;
  const healthStealPct = ability.stats?.HealthStealPct;
  const shockRadius = ability.stats?.ShockRadius;
  const maxLifetime = ability.stats?.MaxLifetime;
  const shockDelay = ability.stats?.ShockDelay;
  const damagePerChain = ability.stats?.DamagePerChain;
  const chainCount = ability.stats?.ChainCount;
  const chainRadius = ability.stats?.ChainRadius;
  const initialRadius = ability.stats?.InitialRadius;
  const bulletResistOnActive = ability.stats?.BulletResistOnActive;
  const regenIncomingDamagePercent = ability.stats?.RegenIncomingDamagePercent;
  const regenIncomingDamageDuration = ability.stats?.RegenIncomingDamageDuration;
  const stompRange = ability.stats?.StompRange;
  const stompWidth = ability.stats?.StompWidth;
  const voidTime = ability.stats?.VoidTime;
  const allyDistance = ability.stats?.AllyDistance;
  const fireRateBonus = ability.stats?.FireRateBonus;
  const healingPerSecond = ability.stats?.HealingPerSecond;
  const shareWithFriendsRadius = ability.stats?.ShareWithFriendsRadius;
  const damagePercentHealth = ability.stats?.DamagePercentHealth;
  const vacuumRadius = ability.stats?.VacuumRadius;
  const bonusHealthOnKill = ability.stats?.BonusHealthOnKill;
  const spinDuration = ability.stats?.SpinDuration;
  const damageHealMult = ability.stats?.DamageHealMult;
  const damageHealMultNonHero = ability.stats?.DamageHealMultNonHero;
  const ampPercentPerStack = ability.stats?.AmpPercentPerStack;
  const damagePerProjectile = ability.stats?.DamagePerProjectile;
  const moveSlowPercent = ability.stats?.MoveSlowPercent;
  const projectileAmount = ability.stats?.ProjectileAmount;
  const ampDuration = ability.stats?.AmpDuration;
  const spiritDamage = ability.stats?.SpiritDamage;
  const throwDistance = ability.stats?.ThrowDistance;
  const healPctVsHeroes = ability.stats?.HealPctVsHeroes;
  const targetingConeAngle = ability.stats?.TargetingConeAngle;
  const healPctVsNonHeroes = ability.stats?.HealPctVsNonHeroes;
  const jumpVelocity = ability.stats?.JumpVelocity;
  const stompDamage = ability.stats?.StompDamage;
  const stompDamagePerMeterPrimary = ability.stats?.StompDamagePerMeterPrimary;
  const bonusTechPowerPerKill = ability.stats?.BonusTechPowerPerKill;
  const tetherDuration = ability.stats?.TetherDuration;
  const lifetime = ability.stats?.Lifetime;
  const armTime = ability.stats?.ArmTime;
  const bulletSplitShot = ability.stats?.BulletSplitShot;
  const weaponDamageBonus = ability.stats?.WeaponDamageBonus;

  return (
    <div className="tooltip-content ability">
      <div className="tooltip-header">
        <div className="ability-icon-container">
          <img
            src={`https://static.bigbrain.gg/deadlock/staticdata/panorama/images${abilityIcon}.png`}
            alt={ability.name}
            className="tooltip-icon"
          />
        </div>
        <div>
          <span className="tooltip-ability-name">{ability.name}</span>
          <div className="cooldown-section">
            {shouldDisplayStat(cooldown) && (
              <div className="tooltip-stat">
                <img src="https://static.bigbrain.gg/deadlock/image-assets/cooldown-icon.png" alt="Cooldown Icon" />
                <span className="stat-value">{cooldown}s</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="tooltip-stats">
        {shouldDisplayStat(DPS) && (
          <div className="tooltip-stat">
            <span className="stat-value">{DPS}</span>
            <span className="stat-name"> DPS</span>
          </div>
        )}
        {shouldDisplayStat(damageAmplification) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damageAmplification}%</span>
            <span className="stat-name"> Damage Amplification</span>
          </div>
        )}
        {shouldDisplayStat(damage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damage}</span>
            <span className="stat-name"> Damage</span>
          </div>
        )}
        {shouldDisplayStat(impactDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{impactDamage}</span>
            <span className="stat-name"> Damage</span>
          </div>
        )}
        {shouldDisplayStat(pulseDPS) && (
          <div className="tooltip-stat">
            <span className="stat-value">{pulseDPS}</span>
            <span className="stat-name"> Damage</span>
          </div>
        )}
        {shouldDisplayStat(damagePerChain) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePerChain}</span>
            <span className="stat-name"> Shock Damage</span>
          </div>
        )}
        {shouldDisplayStat(chainCount) && (
          <div className="tooltip-stat">
            <span className="stat-value">{chainCount}</span>
            <span className="stat-name"> Max Jumps</span>
          </div>
        )}
        {shouldDisplayStat(chainRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{chainRadius}</span>
            <span className="stat-name"> Jump Radius</span>
          </div>
        )}
        {shouldDisplayStat(healthStealPctHero) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healthStealPctHero}%</span>
            <span className="stat-name"> Hero Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(healPctVsHeroes) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healPctVsHeroes}%</span>
            <span className="stat-name"> Heal vs Heroes</span>
          </div>
        )}
        {shouldDisplayStat(targetingConeAngle) && (
          <div className="tooltip-stat">
            <span className="stat-value">{targetingConeAngle}&deg;</span>
            <span className="stat-name"> Attack Angle</span>
          </div>
        )}
        {shouldDisplayStat(healPctVsNonHeroes) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healPctVsNonHeroes}%</span>
            <span className="stat-name"> Heal vs Non-Heroes</span>
          </div>
        )}
        {shouldDisplayStat(bulletResistReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bulletResistReduction}%</span>
            <span className="stat-name"> Bullet Resist Reduction</span>
          </div>
        )}
        {shouldDisplayStat(bleedDPSPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bleedDPSPerStack}</span>
            <span className="stat-name"> Bleed DPS Per Knife</span>
          </div>
        )}
        {shouldDisplayStat(throwDistance) && (
          <div className="tooltip-stat">
            <span className="stat-value">{throwDistance}</span>
            <span className="stat-name"> Max Throw Distance</span>
          </div>
        )}
        {shouldDisplayStat(bleedDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bleedDuration}s</span>
            <span className="stat-name"> Bleed Duration</span>
          </div>
        )}
        {shouldDisplayStat(damagePctDeferred) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePctDeferred}%</span>
            <span className="stat-name"> Incoming Damage Deferred</span>
          </div>
        )}
        {shouldDisplayStat(deferredDamageDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{deferredDamageDuration}s</span>
            <span className="stat-name"> Deferred Damage Duration</span>
          </div>
        )}
        {shouldDisplayStat(deferClearPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{deferClearPct}s</span>
            <span className="stat-name"> Deferred Damage Cleared</span>
          </div>
        )}
        {shouldDisplayStat(damagePctDeferredMaxRage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePctDeferredMaxRage}%</span>
            <span className="stat-name"> Incomming Damage Deferred (Full Rage)</span>
          </div>
        )}
        {shouldDisplayStat(techArmorDamageReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">{techArmorDamageReduction}%</span>
            <span className="stat-name"> Spirit Resist</span>
          </div>
        )}
        {shouldDisplayStat(techShieldPoints) && (
          <div className="tooltip-stat">
            <span className="stat-value">{techShieldPoints}</span>
            <span className="stat-name"> Spirit Shield Health</span>
          </div>
        )}
        {shouldDisplayStat(moveSpeedBonusPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{moveSpeedBonusPct}%</span>
            <span className="stat-name"> Move Speed Bonus</span>
          </div>
        )}
        {shouldDisplayStat(landingDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{landingDamage}</span>
            <span className="stat-name"> Area Damage</span>
          </div>
        )}
        {shouldDisplayStat(regenIncomingDamagePercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{regenIncomingDamagePercent}%</span>
            <span className="stat-name"> Damage Regenerated</span>
          </div>
        )}
        {shouldDisplayStat(regenIncomingDamageDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{regenIncomingDamageDuration}s</span>
            <span className="stat-name"> Regeneration Time</span>
          </div>
        )}
        {shouldDisplayStat(stompRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{stompRange}</span>
            <span className="stat-name"> Stomp Range</span>
          </div>
        )}
        {shouldDisplayStat(stompWidth) && (
          <div className="tooltip-stat">
            <span className="stat-value">{stompWidth}</span>
            <span className="stat-name"> Ability Width</span>
          </div>
        )}
        {shouldDisplayStat(speedBurstSpeed) && (
          <div className="tooltip-stat">
            <span className="stat-value">{speedBurstSpeed}/s</span>
            <span className="stat-name"> Max Dash Speed</span>
          </div>
        )}
        {shouldDisplayStat(jumpVelocity) && (
          <div className="tooltip-stat">
            <span className="stat-value">{jumpVelocity}/s</span>
            <span className="stat-name"> Jump Velocity</span>
          </div>
        )}
        {shouldDisplayStat(lowHealthEnemyDamageBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">{lowHealthEnemyDamageBonus}</span>
            <span className="stat-name"> Max Bonus Damage</span>
          </div>
        )}
        {shouldDisplayStat(bonusGoldOnKill) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bonusGoldOnKill}</span>
            <span className="stat-name"> Bonus Souls Per Assassination</span>
          </div>
        )}
        {shouldDisplayStat(damageHealMult) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damageHealMult}x</span>
            <span className="stat-name"> Hero Damage to Heal</span>
          </div>
        )}
        {shouldDisplayStat(damageHealMultNonHero) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damageHealMultNonHero}x</span>
            <span className="stat-name"> Damage to Heal</span>
          </div>
        )}
        {shouldDisplayStat(timeToFullCharge) && (
          <div className="tooltip-stat">
            <span className="stat-value">{timeToFullCharge}s</span>
            <span className="stat-name"> Full Charge Time</span>
          </div>
        )}
        {shouldDisplayStat(minChargeDamagePercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minChargeDamagePercent}%</span>
            <span className="stat-name"> No Charge Damage</span>
          </div>
        )}
        {shouldDisplayStat(flameAuraDPS) && (
          <div className="tooltip-stat">
            <span className="stat-value">{flameAuraDPS}</span>
            <span className="stat-name"> DPS</span>
          </div>
        )}
        {shouldDisplayStat(tetherDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{tetherDuration}s</span>
            <span className="stat-name"> Tether Duration</span>
          </div>
        )}
        {shouldDisplayStat(lifetime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{lifetime}s</span>
            <span className="stat-name"> Lifetime</span>
          </div>
        )}
        {shouldDisplayStat(armTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{armTime}s</span>
            <span className="stat-name"> Arm Time</span>
          </div>
        )}
        {shouldDisplayStat(magicDamagePerBullet) && (
          <div className="tooltip-stat">
            <span className="stat-value">{magicDamagePerBullet}</span>
            <span className="stat-name"> Spirit Damage Per Bullet</span>
          </div>
        )}
        {shouldDisplayStat(fullChargeDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{fullChargeDamage}</span>
            <span className="stat-name"> Full Charge Damage</span>
          </div>
        )}
        {shouldDisplayStat(slashLength) && (
          <div className="tooltip-stat">
            <span className="stat-value">{slashLength}</span>
            <span className="stat-name"> Slash Length</span>
          </div>
        )}
        {shouldDisplayStat(shockRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{shockRadius}</span>
            <span className="stat-name"> Radius</span>
          </div>
        )}
        {shouldDisplayStat(maxLifetime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxLifetime}s</span>
            <span className="stat-name"> Lifetime</span>
          </div>
        )}
        {shouldDisplayStat(dashTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{dashTime}s</span>
            <span className="stat-name"> Dash Time</span>
          </div>
        )}
        {shouldDisplayStat(bonusHealthOnKill) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bonusHealthOnKill}s</span>
            <span className="stat-name"> Bonus Max Health Per Kill</span>
          </div>
        )}
        {shouldDisplayStat(dashSpeed) && (
          <div className="tooltip-stat">
            <span className="stat-value">{dashSpeed}/s</span>
            <span className="stat-name"> Dash Speed</span>
          </div>
        )}
        {shouldDisplayStat(dashRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{dashRange}</span>
            <span className="stat-name"> Dash Range</span>
          </div>
        )}
        {shouldDisplayStat(groundFlameDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{groundFlameDuration}s</span>
            <span className="stat-name"> Trail Duration</span>
          </div>
        )}
        {shouldDisplayStat(ampPercentPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">{ampPercentPerStack}%</span>
            <span className="stat-name"> Amp Per Stack</span>
          </div>
        )}
        {shouldDisplayStat(damagePerProjectile) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePerProjectile}</span>
            <span className="stat-name"> Damager Per Projectile</span>
          </div>
        )}
        {shouldDisplayStat(moveSlowPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{moveSlowPercent}%</span>
            <span className="stat-name"> Movement Slow</span>
          </div>
        )}
        {shouldDisplayStat(projectileAmount) && (
          <div className="tooltip-stat">
            <span className="stat-value">{projectileAmount}</span>
            <span className="stat-name"> Projectile Amount</span>
          </div>
        )}
        {shouldDisplayStat(ampDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{ampDuration}s</span>
            <span className="stat-name"> Amp Duration</span>
          </div>
        )}
        {shouldDisplayStat(flameAuraRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{flameAuraRadius}</span>
            <span className="stat-name"> Trail Width</span>
          </div>
        )}
        {shouldDisplayStat(swapTotalDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{swapTotalDamage}</span>
            <span className="stat-name"> Swap Damage</span>
          </div>
        )}
        {shouldDisplayStat(spellLifesteal) && (
          <div className="tooltip-stat">
            <span className="stat-value">{spellLifesteal}%</span>
            <span className="stat-name"> Spell Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(stompDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{stompDamage}</span>
            <span className="stat-name"> Stomp Damage</span>
          </div>
        )}
        {shouldDisplayStat(stompDamagePerMeterPrimary) && (
          <div className="tooltip-stat">
            <span className="stat-value">{stompDamagePerMeterPrimary}</span>
            <span className="stat-name"> Damage Per Meter</span>
          </div>
        )}
        {shouldDisplayStat(dotHealthPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{dotHealthPercent}%/sec</span>
            <span className="stat-name"> Bleed Damage</span>
          </div>
        )}
        {shouldDisplayStat(pulseDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{pulseDamage}</span>
            <span className="stat-name"> Pulse Damage</span>
          </div>
        )}
        {shouldDisplayStat(damageAmplificationPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damageAmplificationPerStack}%</span>
            <span className="stat-name"> Damage Amp Per Stack</span>
          </div>
        )}
        {shouldDisplayStat(abilitySpeedPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{abilitySpeedPct}%</span>
            <span className="stat-name"> Ability Speed</span>
          </div>
        )}
        {shouldDisplayStat(enemyHealthPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{enemyHealthPercent}%</span>
            <span className="stat-name"> Enemy Health Threshold</span>
          </div>
        )}
        {shouldDisplayStat(immobilizeDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{immobilizeDuration}s</span>
            <span className="stat-name"> Immobilize Duration</span>
          </div>
        )}
        {shouldDisplayStat(bulletSplitShot) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bulletSplitShot}</span>
            <span className="stat-name"> Weapon Multishot</span>
          </div>
        )}
        {shouldDisplayStat(weaponDamageBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{weaponDamageBonus}</span>
            <span className="stat-name"> Weapon Damage</span>
          </div>
        )}
        {shouldDisplayStat(escapeTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{escapeTime}s</span>
            <span className="stat-name"> Escape Time</span>
          </div>
        )}
        {shouldDisplayStat(escapeRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{escapeRange}</span>
            <span className="stat-name"> Escape Range</span>
          </div>
        )}
        {shouldDisplayStat(voidTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{voidTime}s</span>
            <span className="stat-name"> Void Time</span>
          </div>
        )}
        {shouldDisplayStat(allyDistance) && (
          <div className="tooltip-stat">
            <span className="stat-value">{allyDistance}</span>
            <span className="stat-name"> Ally Distance</span>
          </div>
        )}
        {shouldDisplayStat(fireRateBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">{fireRateBonus}%</span>
            <span className="stat-name"> Fire Rate Bonus</span>
          </div>
        )}
        {shouldDisplayStat(buffDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{buffDamage}%</span>
            <span className="stat-name"> Full Range Damage Bonus</span>
          </div>
        )}

        {shouldDisplayStat(spinDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{spinDuration}s</span>
            <span className="stat-name"> Spin Duration</span>
          </div>
        )}
        {shouldDisplayStat(bulletResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bulletResist}%</span>
            <span className="stat-name"> Bullet Resist</span>
          </div>
        )}
        {shouldDisplayStat(shadowFormDurationOnKill) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{shadowFormDurationOnKill}s</span>
            <span className="stat-name"> Duration On Kill</span>
          </div>
        )}
        {shouldDisplayStat(techResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{techResist}%</span>
            <span className="stat-name"> Spirit Resist</span>
          </div>
        )}
        {shouldDisplayStat(pulseInterval) && (
          <div className="tooltip-stat">
            <span className="stat-value">{pulseInterval}s</span>
            <span className="stat-name"> Pulse Interval</span>
          </div>
        )}
        {shouldDisplayStat(healthStealPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healthStealPct}%</span>
            <span className="stat-name"> Non-Hero Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(damagePerRocket) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePerRocket}</span>
            <span className="stat-name"> Damage Per Rocket</span>
          </div>
        )}
        {shouldDisplayStat(chainDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{chainDuration}s</span>
            <span className="stat-name"> Tether Duration</span>
          </div>
        )}
        {shouldDisplayStat(captureRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{captureRadius}</span>
            <span className="stat-name"> Capture Radius</span>
          </div>
        )}
        {shouldDisplayStat(chainLength) && (
          <div className="tooltip-stat">
            <span className="stat-value">{chainLength}</span>
            <span className="stat-name"> Tether Length</span>
          </div>
        )}
        {shouldDisplayStat(damagePercentHealth) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damagePercentHealth}%</span>
            <span className="stat-name"> Damage Health Percent</span>
          </div>
        )}
        {shouldDisplayStat(vacuumRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{vacuumRadius}%</span>
            <span className="stat-name"> Singularity Radius</span>
          </div>
        )}
        {shouldDisplayStat(burnDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{burnDuration}s</span>
            <span className="stat-name"> Burn Duration</span>
          </div>
        )}
        {shouldDisplayStat(buildUpBulletPercentPerHit) && (
          <div className="tooltip-stat">
            <span className="stat-value">{buildUpBulletPercentPerHit}%</span>
            <span className="stat-name"> Buildup Per Bullet</span>
          </div>
        )}
        {shouldDisplayStat(critBuildup) && (
          <div className="tooltip-stat">
            <span className="stat-value">{critBuildup}%</span>
            <span className="stat-name"> Headshot Buildup</span>
          </div>
        )}
        {shouldDisplayStat(buildUpDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{buildUpDuration}s</span>
            <span className="stat-name"> Buildup Decay Time</span>
          </div>
        )}
        {shouldDisplayStat(explodeDelay) && (
          <div className="tooltip-stat">
            <span className="stat-value">{explodeDelay}s</span>
            <span className="stat-name"> Explosion Delay</span>
          </div>
        )}
        {shouldDisplayStat(grenadesPerSecond) && (
          <div className="tooltip-stat">
            <span className="stat-value">{grenadesPerSecond}</span>
            <span className="stat-name"> Rockets Per Second</span>
          </div>
        )}
        {shouldDisplayStat(timeScaleDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{timeScaleDuration}s</span>
            <span className="stat-name"> Time Stop Duration</span>
          </div>
        )}
        {shouldDisplayStat(bonusTechPowerPerKill) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bonusTechPowerPerKill}</span>
            <span className="stat-name"> Spirit Power Per Kill</span>
          </div>
        )}
        {shouldDisplayStat(movementSlowPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSlowPct}%</span>
            <span className="stat-name"> Movement Slow</span>
          </div>
        )}
        {shouldDisplayStat(beamLength) && (
          <div className="tooltip-stat">
            <span className="stat-value">{beamLength}</span>
            <span className="stat-name"> Beam Length</span>
          </div>
        )}
        {shouldDisplayStat(beamWidth) && (
          <div className="tooltip-stat">
            <span className="stat-value">{beamWidth}</span>
            <span className="stat-name"> Beam Width</span>
          </div>
        )}
        {shouldDisplayStat(timeWallWidth) && (
          <div className="tooltip-stat">
            <span className="stat-value">{timeWallWidth}</span>
            <span className="stat-name"> Wall Width</span>
          </div>
        )}
        {shouldDisplayStat(timeWallHeight) && (
          <div className="tooltip-stat">
            <span className="stat-value">{timeWallHeight}</span>
            <span className="stat-name"> Wall Height</span>
          </div>
        )}
        {shouldDisplayStat(explosionRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{explosionRadius}</span>
            <span className="stat-name"> Explosion Radius</span>
          </div>
        )}
        {shouldDisplayStat(minDistance) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minDistance}</span>
            <span className="stat-name"> Min Range</span>
          </div>
        )}
        {shouldDisplayStat(fuseTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{fuseTime}s</span>
            <span className="stat-name"> Fuse Time</span>
          </div>
        )}
        {shouldDisplayStat(maxBonusBulletDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{maxBonusBulletDamage}%</span>
            <span className="stat-name"> Max Damage Amp</span>
          </div>
        )}
        {shouldDisplayStat(maxSlowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxSlowDuration}s</span>
            <span className="stat-name"> Max Stop Duration</span>
          </div>
        )}
        {shouldDisplayStat(speedChange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{speedChange}%</span>
            <span className="stat-name"> Bonus Speed</span>
          </div>
        )}
        {shouldDisplayStat(healFixedHealth) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healFixedHealth}%</span>
            <span className="stat-name"> Heal on Hero Hit</span>
          </div>
        )}
        {shouldDisplayStat(fireRateSlow) && (
          <div className="tooltip-stat">
            <span className="stat-value">{fireRateSlow}%</span>
            <span className="stat-name"> Fire Rate Slow</span>
          </div>
        )}
        {shouldDisplayStat(explodeDebuffDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{explodeDebuffDuration}s</span>
            <span className="stat-name"> Fire Rate Slow Duration</span>
          </div>
        )}
        {shouldDisplayStat(baseBulletDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{baseBulletDamage}</span>
            <span className="stat-name"> Bullet Damage</span>
          </div>
        )}
        {shouldDisplayStat(headshotBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{headshotBonus}%</span>
            <span className="stat-name"> Headshot Bonus</span>
          </div>
        )}
        {shouldDisplayStat(minBonusBulletDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{minBonusBulletDamage}%</span>
            <span className="stat-name"> Min Damage Amp Bonus</span>
          </div>
        )}
        {shouldDisplayStat(minSlowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minSlowDuration}s</span>
            <span className="stat-name"> Min Stop Duration</span>
          </div>
        )}
        {shouldDisplayStat(maxChargeDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxChargeDuration}s</span>
            <span className="stat-name"> Full Charge Time</span>
          </div>
        )}
        {shouldDisplayStat(speedBoostDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{speedBoostDuration}s</span>
            <span className="stat-name"> Speed Duration</span>
          </div>
        )}
        {shouldDisplayStat(turretDPS) && (
          <div className="tooltip-stat">
            <span className="stat-value">{turretDPS}</span>
            <span className="stat-name"> Turret DPS</span>
          </div>
        )}
        {shouldDisplayStat(turretAttackRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{turretAttackRange}</span>
            <span className="stat-name"> Attack Range</span>
          </div>
        )}
        {shouldDisplayStat(turretLifetime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{turretLifetime}s</span>
            <span className="stat-name"> Lifetime</span>
          </div>
        )}
        {shouldDisplayStat(healthSteal) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healthSteal}</span>
            <span className="stat-name"> Health Steal</span>
          </div>
        )}
        {shouldDisplayStat(sleepWakeUpDelay) && (
          <div className="tooltip-stat">
            <span className="stat-value">{sleepWakeUpDelay}s</span>
            <span className="stat-name"> Wake Up Time</span>
          </div>
        )}
        {shouldDisplayStat(minimumSleepTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minimumSleepTime}s</span>
            <span className="stat-name"> Min Sleep Time</span>
          </div>
        )}
        {shouldDisplayStat(sleepDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{sleepDuration}s</span>
            <span className="stat-name"> Sleep Duration</span>
          </div>
        )}
        {shouldDisplayStat(invisFadeToDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{invisFadeToDuration}s</span>
            <span className="stat-name"> Fade Time</span>
          </div>
        )}
        {shouldDisplayStat(spottedRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{spottedRadius}</span>
            <span className="stat-name"> Spot Radius</span>
          </div>
        )}
        {shouldDisplayStat(invisMoveSpeedMod) && (
          <div className="tooltip-stat">
            <span className="stat-value">{invisMoveSpeedMod}/s</span>
            <span className="stat-name"> Invis Sprint Speed</span>
          </div>
        )}
        {shouldDisplayStat(externalBonusHealthRegen) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{externalBonusHealthRegen}/sec</span>
            <span className="stat-name"> Health Regen</span>
          </div>
        )}
        {shouldDisplayStat(healRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healRadius}</span>
            <span className="stat-name"> Heal Radius</span>
          </div>
        )}
        {shouldDisplayStat(healthToDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healthToDamage}</span>
            <span className="stat-name"> Health to Damage</span>
          </div>
        )}
        {shouldDisplayStat(numBloodShards) && (
          <div className="tooltip-stat">
            <span className="stat-value">{numBloodShards}</span>
            <span className="stat-name"> Blood Shards</span>
          </div>
        )}

        {shouldDisplayStat(damageBonusFixedPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">{damageBonusFixedPerStack}</span>
            <span className="stat-name"> Weapon Damage Per Stack</span>
          </div>
        )}
        {shouldDisplayStat(maxStacks) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxStacks}</span>
            <span className="stat-name"> Max Stacks</span>
          </div>
        )}
        {shouldDisplayStat(vulnerabilityPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{vulnerabilityPerStack}%</span>
            <span className="stat-name"> Damage Amplification Per Stack</span>
          </div>
        )}
        {shouldDisplayStat(moveSpeedPenaltyPerStack) && (
          <div className="tooltip-stat">
            <span className="stat-value">{moveSpeedPenaltyPerStack}%</span>
            <span className="stat-name"> Movement Slow Per Stack</span>
          </div>
        )}
        {shouldDisplayStat(lifeDrainPerSecond) && (
          <div className="tooltip-stat">
            <span className="stat-value">{lifeDrainPerSecond}</span>
            <span className="stat-name"> Damage Per Second</span>
          </div>
        )}
        {shouldDisplayStat(lifeDrainHealthMult) && (
          <div className="tooltip-stat">
            <span className="stat-value">{lifeDrainHealthMult}%</span>
            <span className="stat-name"> Damage to Heal</span>
          </div>
        )}
        {shouldDisplayStat(enemyMinHealthPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{enemyMinHealthPct}%</span>
            <span className="stat-name"> Enemy Min Health</span>
          </div>
        )}
        {shouldDisplayStat(minHealthTakenPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minHealthTakenPct}%</span>
            <span className="stat-name"> Min Health Received</span>
          </div>
        )}
        {shouldDisplayStat(maxRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxRange}</span>
            <span className="stat-name"> Max Tether Range</span>
          </div>
        )}
        {shouldDisplayStat(procDamageBase) && (
          <div className="tooltip-stat">
            <span className="stat-value">{procDamageBase}</span>
            <span className="stat-name"> Base Damage</span>
          </div>
        )}
        {shouldDisplayStat(explodeDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{explodeDamage}</span>
            <span className="stat-name"> Explode Damage</span>
          </div>
        )}
        {shouldDisplayStat(selfDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{selfDamage}</span>
            <span className="stat-name"> Self Damage</span>
          </div>
        )}
        {shouldDisplayStat(armingDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{armingDuration}s</span>
            <span className="stat-name"> Arming Duration</span>
          </div>
        )}
        {shouldDisplayStat(airDropBulletShield) && (
          <div className="tooltip-stat">
            <span className="stat-value">{airDropBulletShield}</span>
            <span className="stat-name"> Bullet Shield</span>
          </div>
        )}
        {shouldDisplayStat(flyingBulletResist) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{flyingBulletResist}%</span>
            <span className="stat-name"> Ally Bullet Resist</span>
          </div>
        )}
        {shouldDisplayStat(buffDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{buffDuration}s</span>
            <span className="stat-name"> Shield Duration</span>
          </div>
        )}
        {shouldDisplayStat(onLandDamageRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{onLandDamageRadius}</span>
            <span className="stat-name"> Explode Radius</span>
          </div>
        )}
        {shouldDisplayStat(bonusHealthRegen) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusHealthRegen}</span>
            <span className="stat-name"> Health Regen</span>
          </div>
        )}
        {shouldDisplayStat(initialRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{initialRadius}</span>
            <span className="stat-name"> Initial Radius</span>
          </div>
        )}
        {shouldDisplayStat(bulletResistOnActive) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bulletResistOnActive}%</span>
            <span className="stat-name"> Bullet Resist</span>
          </div>
        )}
        {shouldDisplayStat(healingPerSecond) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healingPerSecond}/s</span>
            <span className="stat-name"> Health Restored</span>
          </div>
        )}
        {shouldDisplayStat(shareWithFriendsRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{shareWithFriendsRadius}</span>
            <span className="stat-name"> Friendly Heal Radius</span>
          </div>
        )}
        {shouldDisplayStat(abilityChannelTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{abilityChannelTime}s</span>
            <span className="stat-name"> Channel Duration</span>
          </div>
        )}
        {shouldDisplayStat(bonusMoveSpeed) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusMoveSpeed}/s</span>
            <span className="stat-name"> Move Speed</span>
          </div>
        )}
        {shouldDisplayStat(bonusFireRate) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bonusFireRate}%</span>
            <span className="stat-name"> Fire Rate</span>
          </div>
        )}
        {shouldDisplayStat(spiritDamage) && (
          <div className="tooltip-stat">
            <span className="stat-value">{spiritDamage}</span>
            <span className="stat-name"> Spirit Damage</span>
          </div>
        )}
        {shouldDisplayStat(bulletLifestealPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{bulletLifestealPercent}%</span>
            <span className="stat-name"> Bullet Lifesteal</span>
          </div>
        )}
        {shouldDisplayStat(tetherSharedHealPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{tetherSharedHealPct}%</span>
            <span className="stat-name"> Replicated Healing</span>
          </div>
        )}
        {shouldDisplayStat(totalTetherTargets) && (
          <div className="tooltip-stat">
            <span className="stat-value">{totalTetherTargets}</span>
            <span className="stat-name"> Tether Count</span>
          </div>
        )}
        {shouldDisplayStat(movementSpeedBonusDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSpeedBonusDuration}s</span>
            <span className="stat-name"> Move Speed Duration</span>
          </div>
        )}
        {shouldDisplayStat(weaponPowerDebuff) && (
          <div className="tooltip-stat">
            <span className="stat-value">{weaponPowerDebuff}%</span>
            <span className="stat-name"> Weapon Damage</span>
          </div>
        )}
        {shouldDisplayStat(moveSpeedSlowPct) && (
          <div className="tooltip-stat">
            <span className="stat-value">{moveSpeedSlowPct}%</span>
            <span className="stat-name"> Move Speed Slow</span>
          </div>
        )}
        {shouldDisplayStat(moveSpeedBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{moveSpeedBonus}/s</span>
            <span className="stat-name"> Movement Speed</span>
          </div>
        )}
        {shouldDisplayStat(sprintSpeedBonus) && (
          <div className="tooltip-stat">
            <span className="stat-value">+{sprintSpeedBonus}/s</span>
            <span className="stat-name"> Sprint Speed</span>
          </div>
        )}
        {shouldDisplayStat(victimStackDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{victimStackDuration}s</span>
            <span className="stat-name"> Multiplier Duration</span>
          </div>
        )}
        {shouldDisplayStat(revealDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{revealDuration}s</span>
            <span className="stat-name"> Reveal Duration</span>
          </div>
        )}
        {shouldDisplayStat(icePathAuraDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{icePathAuraDuration}%</span>
            <span className="stat-name"> Ice Trail Duration</span>
          </div>
        )}
        {shouldDisplayStat(movementSlow) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSlow}%</span>
            <span className="stat-name"> Movement Slow</span>
          </div>
        )}
        {shouldDisplayStat(movementSlowActual) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSlowActual}%</span>
            <span className="stat-name"> Movement Slow</span>
          </div>
        )}
        {shouldDisplayStat(puddleDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{puddleDuration}s</span>
            <span className="stat-name"> Puddle Duration</span>
          </div>
        )}
        {shouldDisplayStat(impactDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{impactDuration}s</span>
            <span className="stat-name"> Slow Duration</span>
          </div>
        )}
        {shouldDisplayStat(movementSlowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{movementSlowDuration}s</span>
            <span className="stat-name"> Slow Duration</span>
          </div>
        )}
        {shouldDisplayStat(minRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{minRange}</span>
            <span className="stat-name"> Minimum Range</span>
          </div>
        )}
        {shouldDisplayStat(wallImpactRange) && (
          <div className="tooltip-stat">
            <span className="stat-value">{wallImpactRange}</span>
            <span className="stat-name"> Impact Range</span>
          </div>
        )}
        {shouldDisplayStat(slowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{slowDuration}s</span>
            <span className="stat-name"> Slow Duration</span>
          </div>
        )}
        {shouldDisplayStat(bulletArmorReduction) && (
          <div className="tooltip-stat">
            <span className="stat-value">{bulletArmorReduction}%</span>
            <span className="stat-name"> Bullet Resist</span>
          </div>
        )}
        {shouldDisplayStat(launchWindowDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{launchWindowDuration}s</span>
            <span className="stat-name"> Launch Window</span>
          </div>
        )}
        {shouldDisplayStat(debuffDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{debuffDuration}s</span>
            <span className="stat-name"> Debuff Duration</span>
          </div>
        )}
        {shouldDisplayStat(healingFactor) && (
          <div className="tooltip-stat">
            <span className="stat-value">{healingFactor}%</span>
            <span className="stat-name"> Heal vs Heroes</span>
          </div>
        )}
        {shouldDisplayStat(nonHeroHealingFactor) && (
          <div className="tooltip-stat">
            <span className="stat-value">{nonHeroHealingFactor}%</span>
            <span className="stat-name"> Heal vs Non-Heroes</span>
          </div>
        )}
        {shouldDisplayStat(holdInPlaceDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{holdInPlaceDuration}s</span>
            <span className="stat-name"> Lift Duration</span>
          </div>
        )}
        {shouldDisplayStat(whirlwindEvasionChance) && (
          <div className="tooltip-stat">
            <span className="stat-value">{whirlwindEvasionChance}%</span>
            <span className="stat-name"> Bullet Evasion Chance</span>
          </div>
        )}
        {shouldDisplayStat(whirlwindDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{whirlwindDuration}s</span>
            <span className="stat-name"> Bullet Evasion Duration</span>
          </div>
        )}
        {shouldDisplayStat(stunDuration) && (
          <div className="tooltip-stat">
            <span className="stat-value">{stunDuration}s</span>
            <span className="stat-name"> Stun Duration</span>
          </div>
        )}
        {shouldDisplayStat(impactRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{impactRadius}</span>
            <span className="stat-name"> Impact Radius</span>
          </div>
        )}

        {shouldDisplayStat(shockDelay) && (
          <div className="tooltip-stat">
            <span className="stat-value">{shockDelay}s</span>
            <span className="stat-name"> Delay Before Stun</span>
          </div>
        )}
        {shouldDisplayStat(ballRadius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{ballRadius}</span>
            <span className="stat-name"> Ball Radius</span>
          </div>
        )}
        {shouldDisplayStat(maxHealthRegen) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxHealthRegen}%</span>
            <span className="stat-name"> Max Health Heal</span>
          </div>
        )}
        {shouldDisplayStat(maxSlowPercent) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxSlowPercent}%</span>
            <span className="stat-name"> Max Slow</span>
          </div>
        )}
        {shouldDisplayStat(maxSlowTime) && (
          <div className="tooltip-stat">
            <span className="stat-value">{maxSlowTime}s</span>
            <span className="stat-name"> Max Slow Time</span>
          </div>
        )}
        {shouldDisplayStat(pathLength) && (
          <div className="tooltip-stat">
            <span className="stat-value">{pathLength}</span>
            <span className="stat-name"> Path Length</span>
          </div>
        )}
        {shouldDisplayStat(radius) && (
          <div className="tooltip-stat">
            <span className="stat-value">{radius}</span>
            <span className="stat-name"> Radius</span>
          </div>
        )}
        {shouldDisplayStat(launchWindowCooldown) && (
          <div className="tooltip-stat">
            <span className="stat-value">{launchWindowCooldown}s</span>
            <span className="stat-name"> Cooldown</span>
          </div>
        )}
      </div>
      <div className="tooltip-description">
        <p dangerouslySetInnerHTML={{ __html: processedDescription }} />
      </div>
      {(Tier1Description || Tier2Description || Tier3Description || abilityUpgrade1 || abilityUpgrade2 || abilityUpgrade3) && (
        <div className="tooltip-tiered-descriptions">
          {(Tier1Description || abilityUpgrade1) && (
            <div className="flex-wrapper">
              <div className="bolt-image">
                <img src="https://static.bigbrain.gg/deadlock/image-assets/white-bolt.png" alt="Tier 1 Bolt" />
              </div>
              <p
                className="tier-description"
                dangerouslySetInnerHTML={{
                  __html: parseDescriptionText(Tier1Description) || formatUpgradeText(abilityUpgrade1),
                }}
              />
            </div>
          )}
          {(Tier2Description || abilityUpgrade2) && (
            <div className="flex-wrapper">
              <div className="bolt-image">
                <img src="https://static.bigbrain.gg/deadlock/image-assets/white-bolt.png" alt="Tier 2 Bolt" />
              </div>
              <p
                className="tier-description"
                dangerouslySetInnerHTML={{
                  __html: parseDescriptionText(Tier2Description) || formatUpgradeText(abilityUpgrade2),
                }}
              />
            </div>
          )}
          {(Tier3Description || abilityUpgrade3) && (
            <div className="flex-wrapper">
              <div className="bolt-image">
                <img src="https://static.bigbrain.gg/deadlock/image-assets/blue-bolt.png" alt="Tier 3 Bolt" />
              </div>
              <p
                className="tier-description"
                dangerouslySetInnerHTML={{
                  __html: parseDescriptionText(Tier3Description) || formatUpgradeText(abilityUpgrade3),
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AbilityTooltip;
